import { useTranslation } from 'react-i18next';
import FactTile from '../fact-tile/fact-tile';
import { formatNumber } from '@/i18n/format';
import { useRedirectionTarget } from '@/hooks/use-redirection-targets/use-redirection-targets';
import { CallDetail } from '@/services/call-log-service/call-log-service.types';
import { scoreToFive } from '@/utils/score';
import styles from './drawer-facts.module.scss';

interface DrawerFactsProps {
  call: CallDetail;
}

/** The mockup fact grid, 2x2, plus a full-width row for the call identifier. */
export default function DrawerFacts({ call }: DrawerFactsProps) {
  const { t } = useTranslation(['conversations', 'common']);
  // By id, not the whole list: that call cost 200 rows per open and sat on the page cap.
  const { redirectionTarget, isLoading } = useRedirectionTarget(call.redirection_target_id ?? 0);

  // No id means the bot never handed the call off, which is most calls: words, not a dash.
  // Loading gets its own branch, or the tile shows a wrong name first.
  let department: string;
  if (call.redirection_target_id == null) {
    department = t('conversations:drawer.facts.notHandedOff');
  } else if (isLoading) {
    department = t('common:states.loading');
  } else {
    department = redirectionTarget?.name ?? t('conversations:drawer.facts.unknownDepartment');
  }

  const score = scoreToFive(call.sms_score, 'scale10');

  return (
    <div className={styles.grid}>
      <FactTile
        label={t('conversations:drawer.facts.category')}
        value={call.topics?.join(', ') || t('conversations:drawer.facts.uncategorised')}
      />
      <FactTile label={t('conversations:drawer.facts.department')} value={department} />
      <FactTile
        label={t('conversations:drawer.facts.residentRating')}
        value={
          score === null
            ? t('conversations:drawer.facts.notRated')
            : t('conversations:drawer.facts.ratingOutOfFive', { score: formatNumber(score) })
        }
      />
      {/* Two states, not three: the flag is never absent. Compared against false rather
          than read for truthiness, so an absent one reads as its own default instead of
          accusing the call of arriving after hours. */}
      <FactTile
        label={t('conversations:drawer.facts.callTime')}
        value={
          call.business_hours_active === false
            ? t('conversations:drawer.facts.outsideOfficeHours')
            : t('conversations:drawer.facts.duringOfficeHours')
        }
      />
      <FactTile
        className={styles.wide}
        label={t('conversations:drawer.facts.callId')}
        value={call.call_sid || t('conversations:drawer.facts.notRecorded')}
      />
    </div>
  );
}
